import { PencilIcon } from '@/src/assets/icons'
import { EmberChatArea } from '@/src/components/Ember/EmberChatArea'
import { type TabOption, TabSwitcher } from '@/src/components/TabSwitcher'
import { BaseButton } from '@/src/components/buttons/BaseButton'
import { InsightsContext } from '@/src/contexts/InsightsContext'
import { useEmberThread } from '@/src/hooks/useEmberThread.hook'
import { useIsLegacyInsightsNavEnabled } from '@/src/shared/feature-flag/feature-flag.hooks'
import { Box, Flex, Spinner } from '@chakra-ui/react'
import { EmberProfile, FlexCol, FlexRow } from '@components'
import { useEmberStore } from '@features/Ember/infrastructure/useEmberStore'
import { useNavbarCollapsedStore } from '@shared/infrastructure/useNavbarCollapsedStore'
import { getRightContentAreaHeight } from '@shared/utils'
import { EMBER_SIDEBAR_EXPANDED_WIDTH, EMBER_SIDEBAR_WIDTH } from '@utils/constants'
import { InsightsList } from '@widgets/InsightsVideoCta/InsightsList'
import { useInsightsStore } from '@widgets/InsightsVideoCta/useInsightsStore'
import { type FC, useContext, useEffect } from 'react'
import { X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

const ChevronLeft: FC<{ reflectY?: boolean }> = ({ reflectY }) => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='23'
      height='23'
      viewBox='0 0 23 23'
      fill='none'
      aria-label='Ember Icon'
      transform={reflectY ? 'scale(-1, 1)' : undefined}
    >
      <title>Ember</title>
      <g filter='url(#filter0_d_329_22356)'>
        <circle cx='11.5' cy='10.5' r='10.5' fill='white' />
      </g>
      <path d='M12 16L7 11L12 6' stroke='#0A1D3D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' />
      <defs>
        <filter id='filter0_d_329_22356' x='0' y='0' width='23' height='23' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'>
          <feFlood flood-opacity='0' result='BackgroundImageFix' />
          <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
          <feOffset dy='1' />
          <feGaussianBlur stdDeviation='0.5' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.17 0' />
          <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_329_22356' />
          <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_329_22356' result='shape' />
        </filter>
      </defs>
    </svg>
  )
}

const Header: FC = () => {
  const emberStore = useEmberStore()
  const navbarCollapsedStore = useNavbarCollapsedStore()
  const { removeInsight } = useInsightsStore()

  return (
    <FlexRow justifyContent={'space-between'} alignItems={'center'} pl={'20px'} pr={'16px'} py={'10px'}>
      <Box
        position={'absolute'}
        left={'-12px'}
        top={'22.4px'}
        cursor={'pointer'}
        onClick={() => {
          emberStore.toggleExpandSidebar()
          navbarCollapsedStore.setCollapsed(true)
        }}
        _hover={{
          background: '#F4F9FF',
          borderRadius: '50%',
        }}
      >
        {emberStore.isSidebarExpanded ? <ChevronLeft reflectY /> : <ChevronLeft />}
      </Box>
      <FlexRow>
        <Box>
          <EmberProfile />
        </Box>
      </FlexRow>
      <Box
        mt={'2px'}
        cursor={'pointer'}
        onClick={() => {
          emberStore.toggleSidebar()
          removeInsight()
          if (!navbarCollapsedStore.userCollapsedIt) {
            navbarCollapsedStore.setCollapsed(false)
          }
        }}
      >
        <BaseButton variant='secondary' h='28px' w='24px' p='0px' px='6px'>
          <X size={'16px'} color='#848484' />
        </BaseButton>
      </Box>
    </FlexRow>
  )
}

const allTabOptions = [
  { label: 'Insights', value: 'insights' },
  { label: 'Ember Chat', value: 'chat' },
] satisfies { label: string; value: string }[]

type TabValue = (typeof allTabOptions)[number]['value']

const EmberTabs: FC<{ isExpanded: boolean }> = ({ isExpanded }) => {
  const { t } = useTranslation()
  const threadId = useEmberThread('chat')
  const emberStore = useEmberStore()
  const isLegacyInsightsNavEnabled = useIsLegacyInsightsNavEnabled()

  const handleTabChange = (_event: React.ChangeEvent<HTMLElement>, selectedOption: TabOption<TabValue>) => {
    emberStore.setCurrentTabValue(selectedOption.value)
  }

  const newChatButton = (
    <BaseButton
      variant={'secondary'}
      size={'small'}
      onClick={() => emberStore.resetChat(threadId)}
      text={t('new_chat')}
      iconLeft={<PencilIcon />}
      style={{ marginLeft: 'auto' }}
    />
  )

  return (
    <>
      {isExpanded ? (
        <Flex gap='10px' pl='16px' justifyContent='space-between'>
          {isLegacyInsightsNavEnabled && (
            <TabSwitcher colorScheme='blue' options={allTabOptions} onChange={handleTabChange} value={emberStore.currentTabValue} />
          )}
          {newChatButton}
        </Flex>
      ) : (
        <Flex gap='10px' px='16px' alignItems={'center'}>
          {isLegacyInsightsNavEnabled && (
            <TabSwitcher colorScheme='blue' options={allTabOptions} onChange={handleTabChange} value={emberStore.currentTabValue} />
          )}
          {newChatButton}
        </Flex>
      )}
    </>
  )
}

export const EmberSidebar: FC = () => {
  const emberStore = useEmberStore()
  const location = useLocation()
  const insightsCtx = useContext(InsightsContext)
  const isLegacyInsightsNavEnabled = useIsLegacyInsightsNavEnabled()

  useEffect(() => {
    if (emberStore.insight) {
      emberStore.setCurrentTabValue('chat')
    }
  }, [emberStore.insight])

  useEffect(() => {
    emberStore.unsetInsight()
  }, [insightsCtx])

  useEffect(() => {
    if (isLegacyInsightsNavEnabled && location.pathname.includes('/energy-insights')) {
      emberStore.setCurrentTabValue('insights')
    }
  }, [location.pathname])

  return (
    <Box w='400px' h='full' data-name='ember-chat-sidebar'>
      <FlexCol
        right={'0px'}
        h={getRightContentAreaHeight()}
        position={'fixed'}
        width={emberStore.isSidebarExpanded ? EMBER_SIDEBAR_EXPANDED_WIDTH : EMBER_SIDEBAR_WIDTH}
      >
        <FlexCol
          data-name='ember-header'
          borderWidth={'1px'}
          borderColor={'border'}
          borderTopLeftRadius={'8px'}
          borderTopRightRadius={'8px'}
          bg='background'
        >
          <Header />
        </FlexCol>
        <FlexCol
          bg='background'
          borderColor='border'
          borderWidth='1px'
          borderTop={'0px'}
          flexGrow={'1'}
          maxH='calc(100% - 66px)'
          width={'100%'}
          paddingTop='8px'
          borderBottomLeftRadius={'8px'}
          borderBottomRightRadius={'8px'}
          gap={'16px'}
        >
          <EmberTabs isExpanded={emberStore.isSidebarExpanded} />
          <Flex data-name='ember-chat-area' maxH={'calc(100% - 48px)'} flexGrow={'1'}>
            {isLegacyInsightsNavEnabled && emberStore.currentTabValue === 'insights' ? (
              insightsCtx ? (
                <InsightsList
                  insights={insightsCtx as any}
                  bgColor='transparent'
                  cardsBgColor='white'
                  heightOffset='201px'
                  paddingX={'8px'}
                />
              ) : (
                <Flex width={'100%'} height={'100%'} justifyContent={'center'} alignItems={'center'}>
                  <Spinner size='xl' />
                </Flex>
              )
            ) : (
              <EmberChatArea insight={emberStore.insight} />
            )}
          </Flex>
        </FlexCol>
      </FlexCol>
    </Box>
  )
}
